import useUser from '@/hooks/useUser';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { action } from '@/lib/api';
import selectors from '@/redux/selectors/selectors';
import { SESSION_STORAGE_KEY, SESSION_STORAGE_COMPANY_CHANNEL_KEY } from 'shared/schema/constants';

let hasInit = false;
let hasInitChannel = false;

/**
 * Hook to manage companyId with dual system:
 * - Default: Clerk metadata (persists across tabs)
 * - Local: sessionStorage (per-tab, preserved on refresh)
 */
const useCompanyId = () => {
  const user = useUser();
  const defaultCompanyId = user?.jwt?.companyId;
  const defaultCompanyChannel = user?.jwt?.companyChannel;
  const sessionCompanyId = useSelector(selectors.sessionCompanyId);
  const sessionCompanyChannel = useSelector(selectors.sessionCompanyChannel);

  // Initialize Redux state from sessionStorage on mount if empty
  useEffect(() => {
    if (typeof window === 'undefined' || hasInit) {
      return;
    }

    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const currentReduxValue = sessionCompanyId;

    // If sessionStorage has a value but Redux doesn't, set sessionStorage
    if (stored && stored !== currentReduxValue) {
      action('SET_SESSION_COMPANY_ID', stored);
    }
    // If sessionStorage is empty but we have a default companyId, set both
    else if (!stored && defaultCompanyId && !currentReduxValue) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, defaultCompanyId);
      action('SET_SESSION_COMPANY_ID', defaultCompanyId);
    }

    hasInit = true;
  }, []);

  // Initialize Redux state from sessionStorage on mount if empty (companyChannel)
  useEffect(() => {
    if (typeof window === 'undefined' || hasInitChannel) {
      return;
    }

    const stored = sessionStorage.getItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY);
    const currentReduxValue = sessionCompanyChannel;

    // If sessionStorage has a value but Redux doesn't, set sessionStorage
    if (stored && stored !== currentReduxValue) {
      action('SET_SESSION_COMPANY_CHANNEL', stored);
    }
    // If sessionStorage is empty but we have a default companyChannel, set both
    else if (!stored && defaultCompanyChannel && !currentReduxValue) {
      sessionStorage.setItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY, defaultCompanyChannel);
      action('SET_SESSION_COMPANY_CHANNEL', defaultCompanyChannel);
    }

    hasInitChannel = true;
  }, []);

  // Sync sessionStorage when Redux state changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sessionCompanyId) {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored !== sessionCompanyId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionCompanyId);
      }
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [sessionCompanyId]);

  // Sync sessionStorage when Redux state changes (companyChannel)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sessionCompanyChannel) {
      const stored = sessionStorage.getItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY);
      if (stored !== sessionCompanyChannel) {
        sessionStorage.setItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY, sessionCompanyChannel);
      }
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY);
    }
  }, [sessionCompanyChannel]);

  const setSessionCompanyId = useCallback((companyId) => {
    if (typeof window === 'undefined') {
      return;
    }

    // Update Redux state
    action('SET_SESSION_COMPANY_ID', companyId || null);
    action('SET_SESSION_COMPANY_CHANNEL', null);

    // Update sessionStorage immediately
    if (companyId) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, companyId);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
    sessionStorage.removeItem(SESSION_STORAGE_COMPANY_CHANNEL_KEY);
  }, []);

  const getEffectiveCompanyId = useCallback(() => {
    return sessionCompanyId || defaultCompanyId;
  }, [sessionCompanyId, defaultCompanyId]);

  const getEffectiveCompanyChannel = useCallback(() => {
    return sessionCompanyChannel || defaultCompanyChannel;
  }, [sessionCompanyChannel, defaultCompanyChannel]);

  return {
    companyId: getEffectiveCompanyId(),
    defaultCompanyId,
    sessionCompanyId,
    setSessionCompanyId,
    getEffectiveCompanyId,
    companyChannel: getEffectiveCompanyChannel(),
    defaultCompanyChannel,
    sessionCompanyChannel,
    getEffectiveCompanyChannel
  };
};

export default useCompanyId;
